import MarkerClusterer from '@google/markerclusterer';
import debounce from 'lodash.debounce';
import $ from '@vaersaagod/tools/Dom';
import request from '@vaersaagod/tools/request';
import GoogleMapsApi from '../lib/GoogleMapsApi';
import Mapstyle from '../lib/GoogleMapsStyling';

const GEO_ICON = encodeURIComponent(`<svg width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <circle cx="12" cy="12" r="12" fill="#D2E1F7" opacity=".8"/>
    <circle cx="12" cy="12" r="6" fill="#4285F4" stroke="#FFF"/>
    </svg>`);

const DEFAULT_LAT = 59.9141682;
const DEFAULT_LNG = 10.7709212;
const DEFAULT_ZOOM = 14;

export default (el, props) => {
    const dom = $(el);
    const back = dom.find('[data-back]');
    const container = dom.find('[data-map]');
    const markerIds = [];

    let Popup;
    let map;
    let popup;
    let activeMarker;
    let lastQuery;
    let cluster;

    function definePopupClass() {
        Popup = function (position, content) {
            this.el = content;
            this.position = position;
            this.stopEventPropagation();
        };
        Popup.prototype = Object.create(google.maps.OverlayView.prototype);

        Popup.prototype.onAdd = function () {
            this.getPanes().floatPane.appendChild(this.el);
        };

        Popup.prototype.onRemove = function () {
            if (this.el.parentElement) {
                this.el.parentElement.removeChild(this.el);
            }
        };

        Popup.prototype.draw = function () {
            const divPosition = this.getProjection().fromLatLngToDivPixel(this.position);
            const display = Math.abs(divPosition.x) < 4000 && Math.abs(divPosition.y) < 4000 ? 'block' : 'none';
            if (display === 'block') {
                this.el.style.left = `${divPosition.x}px`;
                this.el.style.top = `${divPosition.y}px`;
            }
            if (this.el.style.display !== display) {
                this.el.style.display = display;
            }
        };

        Popup.prototype.stopEventPropagation = function () {
            const element = this.el;
            element.style.cursor = 'auto';
            ['click', 'dblclick', 'contextmenu', 'wheel', 'mousedown', 'touchstart', 'pointerdown'].forEach(event => {
                element.addEventListener(event, e => {
                    e.stopPropagation();
                    e.stopImmediatePropagation();
                });
            });
        };
    }

    const getIcon = item => {
        let icon = 'open';
        if (item) {
            icon = item.levels.length > 1 ? 'both' : item.levels[0].title.toLowerCase();
        }
        return {
            url: `/assets/img/marker-${icon}.png`,
            size: new google.maps.Size(30, 41),
            origin: new google.maps.Point(0, 0),
            anchor: new google.maps.Point(15, 41),
            scaledSize: new google.maps.Size(30, 41)
        };
    };

    const closePopup = () => {
        if (popup) {
            popup.setMap(null);
            popup = null;
            if (activeMarker) {
                activeMarker.setIcon(activeMarker.originalIcon);
                activeMarker = null;
            }
        }
    };

    const getPopupMarkup = item => `<div class="map__dialog">
            <a href="${item.url}" class="map__dialog-wrapper u-box">
                    <div class="d-f fs-11 lh-17 fs-12@m lh-18@m fs-13@l lh-21@l">
                        <p class="c-grey-dark mr-5 mr-10@l">${item.county}</p>
                        <p class="fw-700">${item.numberOfPositions} elever</p>
                    </div>
                    <h2 class="fw-700 fs-13 fs-14@m fs-15@l lh-18 lh-22@m lh-23@l mb-5 mb-10">${item.title}</h2>
                    <div class="d-f">
                        ${item.levels.map(level => `<p class="tag bg-${level.color} mr-8 mr-10@m">${level.title}</p>`).join('')}
                    </div>
                </a>
            </div>`;

    const openPopup = (item, marker) => {
        closePopup();
        const result = $(getPopupMarkup(item));
        activeMarker = marker;
        activeMarker.setIcon(getIcon());
        popup = new Popup(new google.maps.LatLng(item.lat, item.lng), result.get(0));
        popup.setMap(map);
    };

    const addMarkers = (markers = [], shouldOpen = false) => {
        const markersToAdd = [];
        markers.forEach(item => {
            if (markerIds.indexOf(item.id) === -1) {
                const icon = getIcon(item);
                const latlng = new google.maps.LatLng(item.lat, item.lng);
                const marker = new google.maps.Marker({
                    position: latlng,
                    title: item.title,
                    zIndex: 200000 - (parseFloat(item.lat) * 1000),
                    icon
                });
                marker.originalIcon = icon;
                marker.addListener('click', () => {
                    if (activeMarker !== marker) {
                        closePopup();
                        openPopup(item, marker);
                        map.panTo(latlng);
                    } else {
                        closePopup();
                    }
                });
                if (markers.length === 1 && shouldOpen) {
                    google.maps.event.trigger(marker, 'click');
                }
                markerIds.push(item.id);
                markersToAdd.push(marker);
            }
        });
        if (markersToAdd.length) {
            cluster.addMarkers(markersToAdd, true);
            cluster.redraw();
        }
    };

    const addGeoLocation = (center = false) => {
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(position => {
                const pos = {
                    lat: position.coords.latitude,
                    lng: position.coords.longitude
                };

                new google.maps.Marker({
                    position: pos,
                    title: 'Din posisjon',
                    map,
                    icon: {
                        anchor: new google.maps.Point(12, 12),
                        url: `data:image/svg+xml;utf-8, ${GEO_ICON}`
                    }
                });
                if (center) {
                    map.setCenter(pos);
                }
            }, () => {
                console.log('Kunne ikke finne din lokasjon');
            });
        } else {
            console.log('Nettleseren din støtter ikke lokasjonstjenester');
        }
    };

    const isNewQuery = query => !lastQuery || query.lat !== lastQuery.lat || query.lng !== lastQuery.lng || query.radius !== lastQuery.radius;

    const loadMarkers = query => {
        if (isNewQuery(query)) {
            lastQuery = query;
            request
                .get('/jobs.json', { cache: true, ajax: true })
                .set('Accept', 'application/json')
                .query(query)
                .then(res => {
                    if (res.status === 200 && res.body) {
                        addMarkers(res.body);
                    }
                });
        }
    };

    const roundFloat = (value, decimals = 3) => value.toFixed(decimals);

    const boundsUpdated = debounce(() => {
        const center = map.getCenter();
        const bounds = map.getBounds();
        const radius = google.maps.geometry.spherical.computeDistanceBetween(center, bounds.getNorthEast()) / 1000;
        loadMarkers({ lat: roundFloat(center.lat()), lng: roundFloat(center.lng()), radius: roundFloat(radius, 2) });
    }, 500);

    const createMap = () => {
        const initalCenter = (props && props.lat) ? { lat: parseFloat(props.lat), lng: parseFloat(props.lng) } : { lat: DEFAULT_LAT, lng: DEFAULT_LNG };

        map = new google.maps.Map(container.get(0), {
            center: initalCenter,
            zoom: DEFAULT_ZOOM,
            styles: Mapstyle,
            zoomControl: true,
            zoomControlOptions: {
                position: google.maps.ControlPosition.RIGHT_BOTTOM
            },
            fullscreenControl: false,
            mapTypeControl: false,
            scaleControl: false,
            streetViewControl: false,
            rotateControl: false,
            gestureHandling: 'greedy'
        });

        ['click', 'dblclick', 'mousedown', 'touchstart', 'pointerdown'].forEach(event => {
            map.addListener(event, closePopup);
        });

        map.addListener('bounds_changed', boundsUpdated);
    };

    const createCluster = () => {
        const clusterStyle = {
            url: '/static/icons/cluster.svg',
            width: 36,
            height: 36,
            anchor: [0, 0],
            textColor: '#fff',
            textSize: 12,
            backgroundPosition: '0 0'
        };

        cluster = new MarkerClusterer(map, [], {
            averageCenter: true,
            styles: [clusterStyle, clusterStyle, clusterStyle, clusterStyle, clusterStyle]
        });
    };

    const setupMap = () => {
        definePopupClass();
        createMap();
        createCluster();

        if (props && props.markers) {
            addMarkers(props.markers, true);
            addGeoLocation();
        } else {
            addGeoLocation(true);
        }
    };

    const init = () => {
        const gmapApi = new GoogleMapsApi();
        gmapApi.load().then(setupMap);
        if ($('html').hasClass('internal-referrer')) {
            back.on('click', e => {
                e.preventDefault();
                window.history.back();
            });
        }
    };

    const destroy = () => {};

    return {
        init,
        destroy
    };
};



// WEBPACK FOOTER //
// ./src/js/components/JobsMap.js